import { Container, Skeleton } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Id } from 'react-toastify';

import TransactionsForm, { TransactionFormData } from '.';
import { useGetAllCupsQuery } from '../../../../api/cups';
import { useGetFruitsQuery } from '../../../../api/fruitsSlice';
import {
  useGetTransactionByIdQuery,
  useUpdateTransactionMutation,
} from '../../../../api/transactionsApi';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { useApiErrorNotification } from '../../../../hooks/useApiErrorNotification';
import { useApiSuccessNotification } from '../../../../hooks/useApiSuccessNotification';
import {
  mapAllTransactionStatusesToMenuItems,
  mapCupsWithDataToMenuItems,
  mapFruitToMenuItems,
} from '../../../../utils/mapToMenuItems';
import setToastIsLoading from '../../../../utils/toastify/setToastIsLoading';
import { routesJam } from '../../../../constants/routes';

export default function EditTransaction() {
  const { id } = useParams();

  const [toastId, setToastId] = useState<Id>('');

  const { data: transaction, isLoading: transactionIsLoading } =
    useGetTransactionByIdQuery(id ?? skipToken);

  const { data: fruitData, isLoading: fruitLoading } = useGetFruitsQuery();
  const { data: cupsWithData, isLoading: cupsWithDataIsLoading } =
    useGetAllCupsQuery();

  const [updateTransaction, { data, error, isLoading: updateIsLoading }] =
    useUpdateTransactionMutation();

  const mappedFruits = mapFruitToMenuItems(fruitData);
  const mappedCups = mapCupsWithDataToMenuItems(cupsWithData);
  const mappedStatus = mapAllTransactionStatusesToMenuItems();

  const isLoading =
    cupsWithDataIsLoading || fruitLoading || transactionIsLoading;

  const handleSubmit = (data: TransactionFormData) => {
    console.log(data.note);
    const transformed = {
      id: Number(id),
      note: data.note,
      status: data.status,
      orderTypeId: Number(data.orderTypeId),
      cupData: data.cupData.map((cup) => ({
        ...cup,
        quantity: -Math.abs(Number(cup.quantity)), // ensure quantity is negative
      })),
    };

    updateTransaction(transformed);
    setToastId(setToastIsLoading(`Sačekaj....`));
  };

  useApiSuccessNotification({
    data,
    message: 'Transakcija uspešno izmenjena',
    toastId,
    navigateToAfter: `/${routesJam.transactions}`,
  });

  useApiErrorNotification({
    error,
    toastId,
  });

  return (
    <Container maxWidth='sm'>
      <HeaderBreadcrumbs
        heading={'Transakcije'}
        links={[
          {
            name: 'Džemovi',
            href: `/${routesJam.transactions}`,
          },
          {
            name: 'Izmeni transakciju',
            href: `${routesJam.transactions}/${id}`,
          },
        ]}
      />
      {isLoading && <Skeleton height={380} variant='rounded' />}
      {!isLoading && transaction && (
        <TransactionsForm
          values={{
            note: transaction.note,
            orderTypeId: String(transaction.orderTypeId),
            status: transaction.status,
            cupData: transaction.cups.map((cup) => ({
              cupId: cup.cupId,
              quantity: Math.abs(cup.quantity),
            })),
          }}
          isLoading={updateIsLoading}
          onSubmit={handleSubmit}
          mappedCups={mappedCups}
          mappedFruits={mappedFruits}
          mappedStatus={mappedStatus}
        />
      )}
    </Container>
  );
}
